import { navigate } from "gatsby-link"
import React, { useCallback, useEffect, useState } from "react"
import { createContext, useContext } from "react"
import dayjs from "dayjs"
import {
  Color_Code,
  STT_R2_Class_Matrix_Access_Failure,
  STT_R2_Class_Matrix_Connection_Failure,
  Service_Stop_Message_Type,
} from "../enums/Service"
import {
  checkOutage,
  customerCheck,
  customerCheckTaas,
  updateCustomer,
  updateCustomerTaas,
  checkOutageTaas,
  getSubscriberInfoByFuzzy,
  iwsFilterDeviceList
} from "../services/diagnose.service"
import {
  CustomerReport,
  DiagnoseResult,
  NttData,
  OpenTicketDetails,
  SIEBEL_GATED,
} from "../types/diagnose.type"
import api from "../utils/api"
import { useAuthContext } from "./AuthContext"
import { ServiceDetailsTaas, useUserDetails } from "./UserContext"
import { RearrangedCustomerAccount } from "../types/customerDetails.type"
import {
  CheckConnectionFailureStatus,
  CheckNetworkAlertStatus,
  filterOpenTickets,
  hasTicketIdentity,
  getBroadbandServiceNumber,
  isAuthFailure,
  getServiceDiagnosability,
} from "../helpers/helper"
import {
  contactRetrieve,
  createCTT,
  createSR,
} from "../services/createTicket.service"
import {
  ContactResponse,
  TMAccount,
  TMContact,
} from "../types/contactRetrieve.type"
import { SRTTServiceAdvisoryTable1 } from "../constants/create_ticket"
import { SRDetails } from "../types/srtt.type"
import { useLoadingData } from "./LoadingContext"
import { checkOutageNtt } from "../helpers/Outage/ntt"
import { RearrangedServiceAccountTaas } from "../types/serviceDetails.type"
import Cookies from "js-cookie"
import { iwsGetHouseholdHealth } from "../services/iwsDiagnose.service"
type DiagnoseStatus = {
  serial: number
  loading: boolean
  success?: boolean
}

type IContext = {
  isLoading: boolean
  diagnoseStatus: Record<string, DiagnoseStatus>
  diagnoseResult?: DiagnoseResult
  //customerReportData?: CustomerReport
  customerReportData?: any
  iwsHealthReportData?: any
  loginId?: string
  closedTicketDetails?: OpenTicketDetails[]
  serialNumber?: number
  nttData?: NttData
  openTicketDetails?: OpenTicketDetails[]
  ServiceNo?: string
  srDetails?: SRDetails
  accountAction?: Service_Stop_Message_Type
  // The customer record the diagnosed service is actually attached to, resolved
  // from the service id. Cases must be created against this one so other
  // channels can find them - see issue #57.
  serviceCustomerRowId?: string
  // Every customer record this customer holds, for looking up existing cases.
  // Neither record alone is complete - see issue #54.
  customerRowIdsForLookup?: string[]
  //activeServiceAccount?: RearrangedCustomerAccount
  activeServiceAccount?: any
  selectedSystemName?: string
  contacts: ContactResponse | undefined
  askNetworkStatus: boolean
  contactList: TMContact[] | undefined
  handleStartDiagnose: (
    serviceAccount?: RearrangedCustomerAccount
  ) => Promise<void>
  handleStartDiagnoseTaas: (serviceAccount?: RearrangedServiceAccountTaas)=>Promise<void>
  setDiagnoseLoading: (loading: boolean) => void
  setAccountAction: (action: Service_Stop_Message_Type | undefined) => void
  handleContinueCheckingChoice: (choice: "online" | "offline") => void
  iwsAccount:number
  macAddress: string
}

const DiagnoseContext = createContext<IContext>({
  isLoading: false,
  diagnoseStatus: {},
  loginId: "",
  customerReportData: undefined,
  iwsHealthReportData: undefined,
  serialNumber: 1,
  diagnoseResult: {
    nttData: undefined,
    openTicket: [],
  },
  contacts: undefined,
  nttData: undefined,
  openTicketDetails: [],
  ServiceNo: undefined,
  srDetails: undefined,
  closedTicketDetails: [],
  accountAction: undefined,
  serviceCustomerRowId: undefined,
  customerRowIdsForLookup: undefined,
  contactList: undefined,
  activeServiceAccount: undefined,
  selectedSystemName:'',
  askNetworkStatus: false,
  handleStartDiagnose: async () => {},
  handleStartDiagnoseTaas: async () => {},
  setDiagnoseLoading: () => {},
  setAccountAction: () => {},
  handleContinueCheckingChoice: () => {},
  iwsAccount: 0,
  macAddress: "",
})

export const useDiagnoseData = () => useContext(DiagnoseContext)

type DiagnoseProviderProps = {
  children: React.ReactNode
}

export const DiagnoseProvider: React.FC<DiagnoseProviderProps> = ({
  children,
}) => {
  const [loading, setDiagnoseLoading] = useState(false)
  const { tokenDetails, taasTokenDetails,bauTokenDetails, logout, userInfo } = useAuthContext()
  const { serviceAccounts, handleSetSelectedServiceAccountTaas, selectedServiceAccountTaas } = useUserDetails()
  const [diagnoseStatus, setDiagnoseStatus] = useState<
    Record<string, DiagnoseStatus>
  >({})
  const { isLoading, setLoading } = useLoadingData()
  const [serialNumber, setSerialNumber] = useState<number>(1)
  const [accountAction, setAccountAction] =
    useState<Service_Stop_Message_Type>()
  const [serviceCustomerRowId, setServiceCustomerRowId] = useState<string>()
  const [customerRowIdsForLookup, setCustomerRowIdsForLookup] =
    useState<string[]>()
  const [ServiceNo, setServiceNo] = useState<string>()
  const [loginId, setLoginId] = useState<string>()
  const [nttData, setNttData] = useState<NttData>()
  const [customerReportData, setCustomerReport] = useState<any>()
  const [iwsHealthReportData, setIwsHealthReportData] = useState<any>()
  const [openTicketDetails, setOpenTicketDetails] =
    useState<OpenTicketDetails[]>()
  const [closedTicketDetails, setClosedTicketDetails] =
    useState<OpenTicketDetails[]>()
  const [activeServiceAccount, setServiceAccount] =
    useState<RearrangedCustomerAccount>();
  const [srDetails, setSRDetails] = useState<SRDetails>()
  const [SRFDetails, setSRFDetails] = useState<SRDetails>()
  const [contactList, setContactList] = useState<TMContact[]>([])
  const [contacts, setContacts] = useState<ContactResponse>()
  const [askNetworkStatus, willAskNetworkStatus] = useState<boolean>(false)
  const [selectedSystemName, setSelectedSystemName] = useState<string>();
  const handleLoading = useCallback((loading: boolean) => {
    setDiagnoseLoading(loading)
  }, [])
  const [iwsAccount, setIwsAccount] = useState<number>(0);
  const [macAddress, setMacAddress] = useState<string>();
  const handleDiagnoseStatus = (
    serial: number,
    loading: boolean,
    success?: boolean
  ) => {
    setSerialNumber(serial)
    setDiagnoseStatus(prevState => {
      const newState = { ...prevState }
      let statePosition = `${serial}`
      if (newState[statePosition]) {
        newState[statePosition].loading = loading
        newState[statePosition].success = success
      } else {
        newState[statePosition] = { serial, loading, success }
      }
      return newState
    })
  }

  const handleStartDiagnoseTaas = async (
    taasServiceAccount?: any
  ) => {
    const selectedServiceAccountTaascons = taasServiceAccount;
    if (!taasTokenDetails) return
    if(taasServiceAccount){
     handleSetSelectedServiceAccountTaas(taasServiceAccount.CustomerAccount,taasServiceAccount.ServiceDetails)
     setServiceAccount(taasServiceAccount);
     setSelectedSystemName(taasServiceAccount?.CustomerAccount.systemName);
      // TaaS reports Status as a letter - "A" active, "M" suspended - not as
      // the words this enum used to be compared against, so the check never
      // fired and a suspended service was allowed to diagnose and raise a CTT.
      // Only a confirmed-active service proceeds; anything else stops here.
      const diagnosability = getServiceDiagnosability(
        selectedServiceAccountTaascons?.ServiceDetails?.Status
      )
      if (diagnosability !== "active") {
        setAccountAction(
          diagnosability === "suspended"
            ? Service_Stop_Message_Type.TOS_Account_Due_To_Voluntary_Suspension
            : Service_Stop_Message_Type.Account_Inactive
        )

        return
      }
    }
    
    setDiagnoseLoading(true)

    setDiagnoseStatus({})
    handleDiagnoseStatus(1, true)
    let ServiceNo
    let ServiceLoginId
    let serviceProductType
    let systemName
    let loginId = ""
    let allServiceNumbers
    let ServiceType
    const updatedServiceAccounts: string[] = []

    selectedServiceAccountTaascons?.ServiceDetails?.Products
    .forEach((data:any) => {
      if (data.SerialNumber.indexOf("@") == -1) {
        ServiceNo = data.SerialNumber
        setServiceNo(ServiceNo)
      } else if(data.SerialNumber.indexOf("@unifi") !== -1 || data.SerialNumber.indexOf("@unifibiz") !== -1) {
        loginId = data.SerialNumber;
        ServiceLoginId = data.SerialNumber;
        serviceProductType = "Unifi";
        systemName = selectedServiceAccountTaascons?.ServiceDetails?.SystemName
      }
      // if (data.ProductName == "Voice") {
      //   ServiceNo = data.SerialNumber
      //   setServiceNo(ServiceNo)
      // } else if (data.ProductName === "Internet") {
      //   ServiceLoginId = data.SerialNumber
      // }
      let sericesNumebrChecking = data.SerialNumber.replace(
        "044241951",
        "6044241951"
      )
      sericesNumebrChecking = sericesNumebrChecking.replace(
        "0341612847",
        "60341612847"
      )
      sericesNumebrChecking = sericesNumebrChecking.replace(
        "089200568",
        "08900200568"
      )
      updatedServiceAccounts.push(sericesNumebrChecking)

      setLoginId(loginId)
    })
    allServiceNumbers = updatedServiceAccounts
    if (ServiceNo) {
      ServiceType = "ServiceNo"
    } else if (ServiceLoginId) {
      ServiceType = "ServiceId"
    }

    const fromDate = dayjs().format("MM/DD/YYYY")

    const previousDate = dayjs().subtract(30, "days").format("MM/DD/YYYY")

    let checkTicketReport: any = [];
    let openTickets: OpenTicketDetails[] = [];
    let customerOpenTicket:any = [];
    if (taasTokenDetails && taasTokenDetails.access_token) {
      // let bodyRequest = {
      //   activityType: "TICKET",
      //   searchSpecByPeriod:
      //     "[Created]>='" + previousDate + "' AND [Created]<='" + fromDate + "'",
      //     bundleRowId: selectedServiceAccountTaas?.CustomerAccount.CustomerRowId,
      //     serviceNumber: selectedServiceAccountTaas?.ServiceDetails.ServiceNumber,
      // }
      // A customer can hold more than one record in ZSmart CRM, and
      // RetrieveCustomerAccount returns only the most recently created one.
      // Cases raised through NOCGRID, LIVE_CHAT and other channels sit under
      // the others, so querying that single id cannot see them - which is how
      // EasyFix came to raise a duplicate 38 seconds after a NOCGRID case on
      // aimanakif70@unifi. Issue #54.
      //
      // Neither id is complete on its own: the resolved record held the NOCGRID
      // cases but not ours, and ours held only ours. So collect every record
      // and union the results rather than swapping one id for another.
      const customerRowIds: string[] = []
      const addCustomerRowId = (value: any) => {
        const id = String(value ?? "").trim()
        if (id && customerRowIds.indexOf(id) === -1) customerRowIds.push(id)
      }
      addCustomerRowId(selectedServiceAccountTaascons?.CustomerAccount?.CustomerRowId)

      const fuzzyKeyword = getBroadbandServiceNumber(
        selectedServiceAccountTaascons?.ServiceDetails?.Products
      )
      if (fuzzyKeyword) {
        try {
          const fuzzyResponse: any = await getSubscriberInfoByFuzzy(
            taasTokenDetails.access_token,
            {
              eventName: "evONOCGRIDGetSubscriberInfobyFuzzyTaaS",
              channel: "NOCGRID",
              keyword: fuzzyKeyword,
              searchType: "servNbr",
              caseCode: "",
              pageIndex: "",
              pageSize: "",
            },
            await randomString(16, true, true, true, false)
          )
          if (isAuthFailure(fuzzyResponse)) {
            // The session is gone. Carrying on would resolve nothing, then find
            // no cases, and let the customer raise a duplicate.
            setDiagnoseLoading(false)
            navigate("/app/error?reason=session")
            return
          }

          const resolved = (fuzzyResponse && fuzzyResponse.customerList) || []
          resolved.forEach((customer: any) => addCustomerRowId(customer?.custId))

          // The customer profile a case must be created against, so NOCGRID,
          // UCC and the other channels can find it. Issue #57.
          //
          // One service can carry more than one customer profile - the previous
          // customer and the current one, for example after a house move, where
          // the line at the old address goes inactive. EasyFix used to take the
          // profile from RetrieveCustomerAccount, which is keyed on the NRIC and
          // returns the most recently created profile - often an inactive one.
          //
          // This lookup is keyed on the SERVICE instead, and we work on the
          // agreed assumption that TaaS returns only ACTIVE profiles. Any entry
          // returned is therefore a live profile for this service, so taking the
          // first is safe even when several come back.
          //
          // The assumption is not contractually confirmed. The note below is the
          // only signal we would get if several are returned, since this service
          // has no server-side logging.
          const activeProfiles = resolved
            .map((customer: any) => String(customer?.custId ?? "").trim())
            .filter((id: string) => !!id)
          if (activeProfiles.length > 1) {
            console.info(
              "Service resolved to " +
                activeProfiles.length +
                " active customer profiles; creating against the first. Issue #57."
            )
          }
          const attached = activeProfiles[0]
          if (attached) setServiceCustomerRowId(attached)
        } catch (error) {
          // Fail open. Losing the extra records means we may miss a case from
          // another channel, which is the behaviour before this change.
          // Blocking ticket creation because a lookup failed would be worse.
        }
      }

      // Querying every customer record returns cases for every service that
      // customer holds, not only the one being diagnosed. Without this filter a
      // customer with an open case on one line would be blocked from reporting
      // a fault on another.
      //
      // A case is matched against every identifier of the selected service -
      // the service number and each product serial - because a case may be
      // raised against the broadband id, the voice number or the bundle.
      const serviceIdentifiers: string[] = []
      const addServiceIdentifier = (value: any) => {
        const id = String(value ?? "").trim().toLowerCase()
        if (id && serviceIdentifiers.indexOf(id) === -1) serviceIdentifiers.push(id)
      }
      addServiceIdentifier(selectedServiceAccountTaascons?.ServiceDetails?.ServiceNumber)
      ;(selectedServiceAccountTaascons?.ServiceDetails?.Products || []).forEach(
        (product: any) => addServiceIdentifier(product?.SerialNumber)
      )

      const mergedSRDetails: any[] = []
      const mergedTTDetails: any[] = []
      let primaryLookupFailed = false

      for (let idIndex = 0; idIndex < customerRowIds.length; idIndex++) {
        const randomstring = await randomString(16, true, true, true, false);
        let bodyRequest = {
            BundleRowId: customerRowIds[idIndex]
        }
        const lookupResponse: any = await customerCheckTaas(
          taasTokenDetails?.access_token,
          bodyRequest,
          randomstring
        );

        // An expired session is not an answer. Without this the 401 body - which
        // carries no Status object - fell through as zero cases and the customer
        // could raise a duplicate of a case nobody had looked for.
        if (isAuthFailure(lookupResponse)) {
          setDiagnoseLoading(false)
          navigate("/app/error?reason=session")
          return
        }

        const statusType = String(lookupResponse?.Status?.Type ?? "").toUpperCase()

        // Only the first id is the one this journey was built on. If that call
        // is rejected the behaviour is unchanged - error out. A failure on an
        // additional record is ignored, so the check degrades to what it did
        // before rather than blocking the customer.
        if (statusType === "NOK") {
          if (idIndex === 0) primaryLookupFailed = true
          continue
        }

        const srDetails = lookupResponse?.Response?.SRDetailsReturn || []
        const ttDetails = lookupResponse?.Response?.TTDetailsReturn || []
        // The two arrays are positionally aligned - both are built from the
        // same TaaS result/list - so they must be merged in step.
        for (let i = 0; i < srDetails.length; i++) {
          const ttDetail = ttDetails[i] || {}
          const caseServiceId = String(ttDetail?.ServiceID ?? "").trim().toLowerCase()

          // Belongs to one of this customer's other services - not our concern.
          if (caseServiceId && serviceIdentifiers.length &&
              serviceIdentifiers.indexOf(caseServiceId) === -1) {
            continue
          }

          // No ServiceID at all. Observed only on account-level cases such as a
          // change of email address, which should never block a fault report.
          // Every service fault in the production captures carries one.
          if (!caseServiceId) {
            continue
          }

          mergedSRDetails.push(srDetails[i])
          mergedTTDetails.push(ttDetail)
        }
      }

      // Share the list so the re-check immediately before creation uses the
      // same set of records, instead of falling back to the single id.
      setCustomerRowIdsForLookup(customerRowIds)

      const newResponse: any = {
        Status: { Type: primaryLookupFailed ? "NOK" : "OK" },
        Response: {
          SystemName: "TaaS",
          SRDetailsReturn: mergedSRDetails,
          TTDetailsReturn: mergedTTDetails,
        },
      }
      
      
      if (newResponse.Status) {
        if (newResponse && newResponse.Status.Type === "OK") {
          checkTicketReport = newResponse.Response.SRDetailsReturn;
          customerOpenTicket = newResponse.Response;
        }
        if (newResponse.Status.Type === "NOK") {
          setDiagnoseLoading(false)
          navigate("/app/error")
          return
        }
      } else if (newResponse.status) {
        if (newResponse.status.type === "NOK") {
          setDiagnoseLoading(false)
          navigate("/app/error")
          return
        }
      }
    }
    // Shared with the re-check that runs again just before the case is created,
    // so both use exactly the same definition of "open".
    openTickets = filterOpenTickets(checkTicketReport);
      if(openTickets.length > 0){
        const index = checkTicketReport && checkTicketReport.length>0 && 
        checkTicketReport.findIndex((i:any) => {
          return i.Status && i.Status !== "Cancelled" && i.Status !== "Closed";
        });
        const ticketData : OpenTicketDetails = 
          {
              "SystemName": "TaaS",
              "AccountNo": "",
              "BillingAccount": "",
              "SRNumber": customerOpenTicket.TTDetailsReturn[index].CaseId,
              "CreatedDate": customerOpenTicket.SRDetailsReturn[index].CreatedDate,
              "ClosedDate": "",
              "Status": customerOpenTicket.SRDetailsReturn[index].Status,
              "TTNumber": customerOpenTicket.TTDetailsReturn[index].CaseId,
              "ServiceId": customerOpenTicket.TTDetailsReturn[index].ServiceID,
              "BillingAccountNickName": "",
              "ActivityType": "TICKET",
              "SymptomCode": "",
              "NttNumber": "",
              "NovaCategory": "",
              "Product": customerOpenTicket.TTDetailsReturn[index].Product,
              "NovaNttStatus": "",
              "TicketType": "TT",
              "TMActions": [],
              "Type": null
          }
      ;
      setOpenTicketDetails([ticketData]);
      }
      
    if (openTickets.length > 0) {
      setDiagnoseLoading(false)
      handleDiagnoseStatus(1, false, false)
      navigate("/app/result")
      return
    }
    handleDiagnoseStatus(1, false, true)
    handleDiagnoseStatus(2, true)

    let date = new Date()
    let year = date.getFullYear().toString().substring(-2)
    let month = date.getMonth() + 1
    let formattedMonth = month > 9 ? `${month}` : `0${month}`
    let day = date.getDate()
    let formattedDay = day > 9 ? `${day}` : `0${day}`
    let formattedHour =
      date.getHours() > 9 ? `${date.getHours()}` : `0${date.getHours()}`
    let formattedMinute =
      date.getMinutes() > 9 ? `${date.getMinutes()}` : `0${date.getMinutes()}`
    let formattedSec =
      date.getSeconds() > 9 ? `${date.getSeconds()}` : `0${date.getSeconds()}`
    let RequestID = `EASYFIX-${year}${formattedMonth}${formattedDay}${formattedHour}${formattedMinute}${formattedSec}-${selectedServiceAccountTaascons?.ServiceDetails.ServiceNumber}`
    // INTT keys on the voice / fixed-line number, not the broadband login. The
    // Interface Agreement (WhatsApp & IVR - INTT v1.0, section 2.1.2) gives
    // ServiceNo as 0077703422 and 60362415638, and its sample request sends
    // "60362415638" - both phone numbers. The NOVA path already sends the Voice
    // product serial for the same reason.
    // The local ServiceNo above is that value: the product serial with no "@",
    // e.g. 60389260188. Note it deliberately shadows the ServiceNo state hook,
    // so this reads the value just computed for this account rather than a stale
    // one. Falls back to the broadband id, then the bundle id, so an account
    // with no voice line still sends something instead of nothing.
    const outageServiceNo =
      ServiceNo ??
      getBroadbandServiceNumber(
        selectedServiceAccountTaascons?.ServiceDetails?.Products
      ) ??
      selectedServiceAccountTaascons?.ServiceDetails.ServiceNumber
    let nttDetails: NttData | undefined = undefined
    // Routed through the NOVA checkOutage proxy, which already points at the
    // live Antrix endpoint (antrix-spice-waba) and already returns the
    // { status, response } shape this code reads. The TaaS OVAL_NEXT route used
    // before is decommissioned - the SOA layer answers with its own interface
    // configuration record carrying activeFlag "N", so the call never reached
    // OVAL. Uses the BAU token, as Antrix expects and as the NOVA path does.
    if (bauTokenDetails && bauTokenDetails?.access_token) {
      const bodyRequest = {
        RequestID: RequestID,
        ServiceNo: outageServiceNo,
      }
      const newResponse:any = await checkOutage(
        bauTokenDetails.access_token,
        bodyRequest
      )
      if (newResponse && newResponse.status === 200)
        nttDetails = newResponse.response
      // setNttData(nttDetails)
    }
    setNttData(nttDetails)
    if (nttDetails && checkOutageNtt(nttDetails)) {
      setDiagnoseLoading(false)
      handleDiagnoseStatus(2, false, false)
      navigate("/app/result")
      return
    }
    handleDiagnoseStatus(2, false, true)
    handleDiagnoseStatus(3, true)

    //let customerReport: CustomerReport | undefined = undefined
    let customerReport: any | undefined = undefined
    if (bauTokenDetails && bauTokenDetails.access_token) {
      const bodyRequest = {
        product: "Unifi",
        serviceID: loginId,
        serviceNo: "",
        iptv: "",
      }
      const randomstringuTaas = await randomString(16, true, true, true, false);
      const newResponse:any = await updateCustomerTaas(
        bauTokenDetails?.access_token,
        bodyRequest,
        randomstringuTaas
      )
      
      
      if (newResponse && newResponse.Status?.Type === "NOK") {
        customerReport = newResponse.Response
        setDiagnoseLoading(false)

        navigate("/app/error")
        return
      }

      if (newResponse && newResponse.Status?.Type === "OK") {
        customerReport = newResponse.Response
      }

    }

    // setOpenTickets(checkTicketReport)
    setCustomerReport(customerReport)
    if (!customerReport) {
      setDiagnoseLoading(false)

      navigate("/app/error")
      return
    }
//     if (customerReport.taas[0].STT_R2_Class_Matrix === "") {

//       const cameliaResponse = customerReport.expert.inbound
//       const cameliaInboundCondition = cameliaResponse
//         ?.split(/\Condition=/)[1]
//         ?.split(" ")[0]
//       //       new logic 1 when STT_R2_Class_Matrix return blank

//       // 1. "Internet_ColourCode": "YELLOW"
//       // 2. CAMELIA Condition = Good OR Caution OR Unstable

//       // Next Flow: Internet Connection Performance
//       // ____________________________________________

//       // new logic 2 when STT_R2_Class_Matrix return blank

//       // 1. "Internet_ColourCode": "GREY"
//       // 2. CAMELIA Condition = Good OR Caution OR Unstable

//       // Next Flow:

//       // 1. Ask user the internet is Online or Offline?

//       // If Online, go to Internet Connection Performance
//       // If Offline, go to Internet Connection Failure

//       // ___________________________________

//       // new logic 3 when STT_R2_Class_Matrix return blank

//       // 1. "Internet_ColourCode": "GREY"
//       // 2. CAMELIA Condition = Failure

//       // Next Flow: Go to ticket creation

//       // Logic 1
//       if (
//         customerReport.expert.Internet_ColourCode === Color_Code.GREY &&
//         cameliaInboundCondition === "Failure"
//       ) {
//         handleDiagnoseStatus(3, false, false)
//       }

//       // Logic 2
//       if (
//         customerReport.expert.Internet_ColourCode === Color_Code.GREY &&
//         (cameliaInboundCondition === "Good" ||
//           cameliaInboundCondition === "Caution" ||
//           cameliaInboundCondition === "Unstable")
//       ) {
//         handleDiagnoseStatus(3, false, false)
//         willAskNetworkStatus(true)
//       }
//       // Logic 3

//       if (
//         customerReport.expert.Internet_ColourCode === Color_Code.YELLOW &&
//         (cameliaInboundCondition === "Good" ||
//           cameliaInboundCondition === "Caution" ||
//           cameliaInboundCondition === "Unstable")
//       ) {
//         handleDiagnoseStatus(3, false, true)
//         handleDiagnoseStatus(4, false, true)
//       }
//     } else {
//       if (customerReport && !customerReport.taas[0].STT_R2_Class_Matrix) {
//         setDiagnoseLoading(false);
//         console.log(customerReport);
//         console.log('part2');
//        // navigate("/app/error")
//         return
//       }
// const networkStatus = CheckNetworkAlertStatus(
//   customerReport.taas[0].STT_R2_Class_Matrix as STT_R2_Class_Matrix_Access_Failure
// );

// const connectionFailureStatus = CheckConnectionFailureStatus(
//   customerReport.taas[0].STT_R2_Class_Matrix as STT_R2_Class_Matrix_Connection_Failure
// );

//       if (
//         customerReport &&
//         CheckNetworkAlertStatus(
//           customerReport.taas[0].STT_R2_Class_Matrix as STT_R2_Class_Matrix_Access_Failure
//         )
//       ) {

//         console.log('part3');
//         handleDiagnoseStatus(3, false, false)
//       } else {
//         handleDiagnoseStatus(3, false, true)
//         console.log('part4');
//         if (
//           customerReport &&
//           CheckConnectionFailureStatus(
//             customerReport.taas[0].STT_R2_Class_Matrix as STT_R2_Class_Matrix_Connection_Failure
//           )
//         ) {
//           console.log('part5');
//           handleDiagnoseStatus(4, false, false)
//         } else {
//           console.log('part6');
//           handleDiagnoseStatus(4, false, true)
//         }
//       }
//     }

    if (customerReport.expert.STT_R2_Class_Matrix === "") {
      const cameliaResponse = customerReport.expert.inbound
      const cameliaInboundCondition = cameliaResponse
        ?.split(/\Condition=/)[1]
        ?.split(" ")[0]
      
  
      // Logic 1
      if (
        customerReport.expert.Internet_ColourCode === Color_Code.GREY &&
        cameliaInboundCondition === "Failure"
      ) {
        handleDiagnoseStatus(3, false, false)
      }
  
      // Logic 2
      if (
        customerReport.expert.Internet_ColourCode === Color_Code.GREY &&
        (cameliaInboundCondition === "Good" ||
          cameliaInboundCondition === "Caution" ||
          cameliaInboundCondition === "Unstable")
      ) {
        handleDiagnoseStatus(3, false, false)
        willAskNetworkStatus(true)
      }
      // Logic 3
  
      if (
        customerReport.expert.Internet_ColourCode === Color_Code.YELLOW &&
        (cameliaInboundCondition === "Good" ||
          cameliaInboundCondition === "Caution" ||
          cameliaInboundCondition === "Unstable")
      ) {
        handleDiagnoseStatus(3, false, true)
        handleDiagnoseStatus(4, false, true)
      }
    } else {
      if (customerReport && !customerReport.expert.STT_R2_Class_Matrix) {
        setDiagnoseLoading(false)
  
        navigate("/app/error")
        return
      }
  
      if (
        customerReport &&
        CheckNetworkAlertStatus(
          customerReport.expert
            .STT_R2_Class_Matrix as STT_R2_Class_Matrix_Access_Failure
        )
      ) {
        handleDiagnoseStatus(3, false, false)
      } else {
        handleDiagnoseStatus(3, false, true)
  
        if (
          customerReport &&
          CheckConnectionFailureStatus(
            customerReport.expert
              .STT_R2_Class_Matrix as STT_R2_Class_Matrix_Connection_Failure
          )
        ) {
          handleDiagnoseStatus(4, false, false)
        } else {
          handleDiagnoseStatus(4, false, true)
        }
      }
    }

    let closedTickets: OpenTicketDetails[] = []
    closedTickets =
      checkTicketReport &&
      checkTicketReport.filter((i:any) => {

        if (i.Status) return i.Status && i.Status === "Closed"
        return false
      })

    setClosedTicketDetails(closedTickets)
    if (closedTickets.length > 0) {
      handleDiagnoseStatus(5, false, false)
      setDiagnoseLoading(false)
      navigate("/app/result")
      return
    }
    handleDiagnoseStatus(5, false, true)

    setDiagnoseLoading(false)
    navigate("/app/result")
    return
  }


const handleStartDiagnose = async (
  serviceAccount?: any,
) => {
  if (!tokenDetails) return
  
if(!serviceAccount) return
setLoading(true, "Just a second while we retrieve your information")
let iwsAccountDiagnose = 0;
let encodedMacAddress = "";
let iwsHealthReport: any | undefined = undefined;
let iwsLoginId = "";
    setServiceAccount(serviceAccount)
    setSelectedSystemName(serviceAccount?.CustomerAccount.systemName);
    // Same allow-list as the TaaS path above. NOVA sends the word form, which
    // getServiceDiagnosability also understands, so both systems now stop on
    // anything that is not an active service.
    const diagnosability = getServiceDiagnosability(
      serviceAccount?.ServiceDetails?.Status
    )
    if (diagnosability !== "active") {
      setAccountAction(
        diagnosability === "suspended"
          ? Service_Stop_Message_Type.TOS_Account_Due_To_Voluntary_Suspension
          : Service_Stop_Message_Type.Account_Inactive
      )
      // The loading overlay was switched on a few lines above. Without this it
      // stayed on top of the message the customer is meant to read.
      setLoading(false, "")

      return
    }
    if (bauTokenDetails?.access_token && bauTokenDetails?.access_token) {
      
      serviceAccount?.ServiceDetails.Products.forEach((data:any) => {
        if (data.ProductName === "Internet") {
          if (data.SerialNumber.indexOf("@") == -1) {
            iwsLoginId = data.SerialNumber + "@streamyx"
          } else {
            iwsLoginId = data.SerialNumber
          }

        }
      })
      if(iwsLoginId != ""){
        const bodyRequest = {
          "filterArray": [
              {
                  "key": "userId",
                  "value": iwsLoginId
              }
          ]
      };
       
      
      const randomstring = await randomString(16, true, true, true, false);
        const newResponse = await iwsFilterDeviceList(
          bauTokenDetails?.access_token,
          bodyRequest,
          randomstring
        )
        if (newResponse.status === 200) {
          if (newResponse?.data && Array.isArray(newResponse.data) && newResponse.data.length > 0) {

            const matchedDevice =   newResponse.data.find(
              (item:any) => item.loginID === iwsLoginId
            );
            if (matchedDevice) {
              //Success - device found
              setIwsAccount(1);
              iwsAccountDiagnose = 1;
              encodedMacAddress = matchedDevice.cID;
              setMacAddress(matchedDevice.cID);
              Cookies.set("iws_account", "1");
            } else {
              //Device not found for this login ID
              setIwsAccount(0);
              iwsAccountDiagnose = 0;
              console.log("no match found");
            }
          } else {
            setIwsAccount(0);
            iwsAccountDiagnose = 0;
            console.log("no match found2");
          }
        } else {
          setIwsAccount(0);
          iwsAccountDiagnose = 0;
          setLoading(false, "");
          navigate("/app/error");
          return;
        }
        
        } else {
          Cookies.set("iws_account", "0");
          setIwsAccount(0);
        }
      }
      setLoading(false, "");

  setDiagnoseLoading(true)

  setDiagnoseStatus({})
  handleDiagnoseStatus(1, true)
  const serviceNumber = serviceAccount?.CustomerAccount.accountNumber
  const services = serviceAccount;
  if (!services) return
  let ServiceNo
  let ServiceLoginId
  let serviceProductType
  let systemName
  let loginId = ""
  let allServiceNumbers
  let ServiceType
  const updatedServiceAccounts: string[] = []

  serviceAccount?.ServiceDetails.Products.forEach((data:any) => {
    if (data.ProductName == "Voice") {
      ServiceNo = data.SerialNumber
      setServiceNo(ServiceNo)
    } else if (data.ProductName === "Internet") {
      ServiceLoginId = data.SerialNumber
    }
    let sericesNumebrChecking = data.SerialNumber.replace(
      "044241951",
      "6044241951"
    )
    sericesNumebrChecking = sericesNumebrChecking.replace(
      "0341612847",
      "60341612847"
    )
    sericesNumebrChecking = sericesNumebrChecking.replace(
      "089200568",
      "08900200568"
    )
    updatedServiceAccounts.push(sericesNumebrChecking)
    if (data.ProductName === "Internet") {
      if (data.SerialNumber.indexOf("@") == -1) {
        loginId = data.SerialNumber + "@streamyx"
      } else {
        loginId = data.SerialNumber
      }
      if (services && services.CustomerAccount.systemName === "NOVA") {
        serviceProductType = "Unifi"
      }

      systemName = services && services.CustomerAccount.systemName
    }
    if (
      services &&
      services.ServiceDetails.ProdPromName === "Telephony"
    ) {
      if (services.CustomerAccount.systemName === "NOVA") {
        serviceProductType = "Unifi"
      }

      systemName = services.CustomerAccount.systemName
      loginId = data.SerialNumber
    }
    setLoginId(loginId)
  })
  allServiceNumbers = updatedServiceAccounts
  if (ServiceNo) {
    ServiceType = "ServiceNo"
  } else if (ServiceLoginId) {
    ServiceType = "ServiceId"
  }

  const fromDate = dayjs().format("MM/DD/YYYY")

  const previousDate = dayjs().subtract(30, "days").format("MM/DD/YYYY")

  let checkTicketReport: OpenTicketDetails[] = []
  let openTickets: OpenTicketDetails[] = []
  if (tokenDetails && tokenDetails.access_token) {
    let bodyRequest = {
      activityType: "TICKET",
      searchSpecByPeriod:
        "[Created]>='" + previousDate + "' AND [Created]<='" + fromDate + "'",
    }
    const newResponse = await customerCheck(
      tokenDetails?.access_token,
      bodyRequest
    )
   
    if (newResponse.Status) {
      if (newResponse && newResponse.Status.Type === "Ok") {
        checkTicketReport = newResponse.Data
      }
      if (newResponse.Status.Type === "NOK") {
        setDiagnoseLoading(false)
        navigate("/app/error")
        return
      }
    } else if (newResponse.status) {
      if (newResponse.status.type === "NOK") {
        setDiagnoseLoading(false)
        navigate("/app/error")
        return
      }
    }
  }
  
  openTickets =
    checkTicketReport &&
    checkTicketReport.filter(i => {
      // Drop i-Engage's placeholder record before anything else - it has no
      // identifier and no date, but Status "Open", so it would otherwise count
      // as an open ticket and block the customer.
      if (!hasTicketIdentity(i)) {
        return false;
      }
      if(dayjs(i.CreatedDate).isBefore(dayjs().subtract(30, "days"))){
        return false;
      }
      if (i.TicketType === "SR")
        return (
          i.Type &&
          i.Type == "Fault" &&
          i.Status &&
          i.Status !== "Cancelled" &&
          i.Status !== "Closed"
        )
      if (i.TicketType === "TT")
        return i.Status && i.Status !== "Cancelled" && i.Status !== "Closed"
      return false
    });

    
  setOpenTicketDetails(openTickets);

  if (openTickets.length > 0) {
    setDiagnoseLoading(false)
    handleDiagnoseStatus(1, false, false)
    navigate("/app/result")
    return
  }
  handleDiagnoseStatus(1, false, true)
  handleDiagnoseStatus(2, true)

  let date = new Date()
  let year = date.getFullYear().toString().substring(-2)
  let month = date.getMonth() + 1
  let formattedMonth = month > 9 ? `${month}` : `0${month}`
  let day = date.getDate()
  let formattedDay = day > 9 ? `${day}` : `0${day}`
  let formattedHour =
    date.getHours() > 9 ? `${date.getHours()}` : `0${date.getHours()}`
  let formattedMinute =
    date.getMinutes() > 9 ? `${date.getMinutes()}` : `0${date.getMinutes()}`
  let formattedSec =
    date.getSeconds() > 9 ? `${date.getSeconds()}` : `0${date.getSeconds()}`
  let RequestID = `EASYFIX-${year}${formattedMonth}${formattedDay}${formattedHour}${formattedMinute}${formattedSec}-${ServiceNo}`
  let nttDetails: any | undefined = undefined
  if(bauTokenDetails?.access_token){
    const bodyRequest = {
      RequestID: RequestID,
      ServiceNo: ServiceNo,
    }
    const newResponse:any = await checkOutage(
      bauTokenDetails.access_token,
      bodyRequest
    )
    if (newResponse && newResponse.status == 200)
      nttDetails = newResponse.response
    // setNttData(nttDetails)
  }
  setNttData(nttDetails)
  if (nttDetails && checkOutageNtt(nttDetails)) {
    setDiagnoseLoading(false)
    handleDiagnoseStatus(2, false, false)
    navigate("/app/result")
    return
  }
  handleDiagnoseStatus(2, false, true)
  handleDiagnoseStatus(3, true)
  //old Product flow 17-11-2024//
  let customerReport: any | undefined = undefined
  
  if (tokenDetails && tokenDetails.access_token) {
    const bodyRequest = {
      product: serviceProductType,
      serviceID: loginId,
      serviceNo: "",
      iptv: "",
    }
    const newResponse = await updateCustomer(
      tokenDetails?.access_token,
      bodyRequest
    );
   
   if (newResponse && newResponse.Status?.Type === "NOK") {
      customerReport = newResponse.Response
      setDiagnoseLoading(false)

      navigate("/app/error")
      return
    }
    if (newResponse && newResponse.Status?.Type === "NOK") {
      customerReport = newResponse.Response
      setDiagnoseLoading(false)

      navigate("/app/error")
      return
    }
    if (newResponse && newResponse.Status?.Type === "OK") {
      customerReport = newResponse.Response
    }
    const randomstring = await randomString(16, true, true, true, false);
    if(iwsAccountDiagnose == 1){
  
      const encodedCID = encodeURIComponent(encodedMacAddress ?? "");
      const healthbodyRequest = {
        cId: encodedCID,
       
      }
      if(bauTokenDetails && bauTokenDetails?.access_token){
       
      const healthResponse:any =  await iwsGetHouseholdHealth(
                  bauTokenDetails?.access_token,
                  healthbodyRequest,
                  randomstring
                );
        if (!healthResponse) {
          setDiagnoseLoading(false);
          navigate("/app/error");
          return;
        }
        if (healthResponse.status !== 200) {
          iwsHealthReport = healthResponse.response;
          setDiagnoseLoading(false);
          navigate("/app/error");
          return;
        }
        if (healthResponse.status == 200){
          iwsHealthReport = healthResponse.response;
        }
      
        
      }
    }
  
  }
  setIwsHealthReportData(iwsHealthReport);
  // setOpenTickets(checkTicketReport)
//   if (newResponse && newResponse?.status?.type === "OK") {
//     nttDetails = newResponse.response;
//   } else if (newResponse?.success === false) {
//     console.error("API failed:", newResponse.error);
//     // show alert or fallback UI
//     navigate("/app/result")
// return
//   }
  setCustomerReport(customerReport)
  if (!customerReport) {
    setDiagnoseLoading(false)

    navigate("/app/error")
    return
  }

  if (customerReport.expert.STT_R2_Class_Matrix === "") {
    const cameliaResponse = customerReport.expert.inbound
    const cameliaInboundCondition = cameliaResponse
      ?.split(/\Condition=/)[1]
      ?.split(" ")[0]
    

    // Logic 1
    if (
      customerReport.expert.Internet_ColourCode === Color_Code.GREY &&
      cameliaInboundCondition === "Failure"
    ) {
      handleDiagnoseStatus(3, false, false)
    }

    // Logic 2
    if (
      customerReport.expert.Internet_ColourCode === Color_Code.GREY &&
      (cameliaInboundCondition === "Good" ||
        cameliaInboundCondition === "Caution" ||
        cameliaInboundCondition === "Unstable")
    ) {
      handleDiagnoseStatus(3, false, false)
      willAskNetworkStatus(true)
    }
    // Logic 3

    if (
      customerReport.expert.Internet_ColourCode === Color_Code.YELLOW &&
      (cameliaInboundCondition === "Good" ||
        cameliaInboundCondition === "Caution" ||
        cameliaInboundCondition === "Unstable")
    ) {
      handleDiagnoseStatus(3, false, true)
      if(iwsAccountDiagnose != 1){
        handleDiagnoseStatus(4, false, true)
      }
    }
  } else {
    if (customerReport && !customerReport.expert.STT_R2_Class_Matrix) {
      setDiagnoseLoading(false)

      navigate("/app/error")
      return
    }

    if (
      customerReport &&
      CheckNetworkAlertStatus(
        customerReport.expert
          .STT_R2_Class_Matrix as STT_R2_Class_Matrix_Access_Failure
      )
    ) {
      handleDiagnoseStatus(3, false, false)
    } else {
      handleDiagnoseStatus(3, false, true)
      if(iwsAccountDiagnose != 1){
        if (
          customerReport &&
          CheckConnectionFailureStatus(
            customerReport.expert
              .STT_R2_Class_Matrix as STT_R2_Class_Matrix_Connection_Failure
          )
        ) {

          handleDiagnoseStatus(4, false, false)
        } else {

          handleDiagnoseStatus(4, false, true)
        }
      } else {

        const hasAlert = iwsHealthReport?.networkHealth?.some(
          (item:any) => item.status === "ALERT"
        );
        if(hasAlert){
          handleDiagnoseStatus(4, false, false);
          handleDiagnoseStatus(5, false, false);
        } else {
          
          handleDiagnoseStatus(4, false, true);
          handleDiagnoseStatus(5, false, true);
        }
      }
    }
  }

  let closedTickets: OpenTicketDetails[] = []
  closedTickets =
    checkTicketReport &&
    checkTicketReport.filter(i => {
      if (i.TicketType === "SR")
        return (
          i.Type && i.Type == "Fault" && i.Status && i.Status === "Closed"
        )
      if (i.TicketType === "TT") return i.Status && i.Status === "Closed"
      return false
    })

  setClosedTicketDetails(closedTickets)
  if(iwsAccountDiagnose != 1){
    if (closedTickets.length > 0) {
      handleDiagnoseStatus(5, false, false)
      setDiagnoseLoading(false)
      navigate("/app/result")
      return
    }
    handleDiagnoseStatus(5, false, true)
  } else {
    if (closedTickets.length > 0) {
      handleDiagnoseStatus(6, false, false)
      setDiagnoseLoading(false)
      navigate("/app/result")
      return
    }
    handleDiagnoseStatus(6, false, true)
  }
  

  setDiagnoseLoading(false)
  navigate("/app/result")
  return
}

  const handleContinueCheckingChoice = (choice: "online" | "offline") => {
    if (choice === "offline") {
      handleDiagnoseStatus(4, false, false)
    }
    if (choice === "online") {
      handleDiagnoseStatus(4, false, true)
    }
    navigate("/app/internet")
  }
  const randomString = async (length:any, includeLowercase = true, includeUppercase = true, includeNumbers = true, includeSpecial = false) =>  {
    let characters = '';
    
    if (includeLowercase) characters += 'abcdefghijklmnopqrstuvwxyz';
    if (includeUppercase) characters += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (includeNumbers) characters += '0123456789';
    if (includeSpecial) characters += '!@#$%^&*()_+[]{}|;:,.<>?';
  
    let result = '';
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  }

  const context = React.useMemo(
    () => ({
      isLoading: loading,
      diagnoseStatus,
      nttData,
      openTicketDetails,
      serialNumber,
      customerReportData,
      iwsHealthReportData,
      loginId,
      ServiceNo,
      accountAction,
      serviceCustomerRowId,
      customerRowIdsForLookup,
      closedTicketDetails,
      activeServiceAccount,
      selectedSystemName,
      contactList,
      contacts,
      srDetails,
      askNetworkStatus,
      setDiagnoseLoading: handleLoading,
      handleStartDiagnose,
      setAccountAction,
      handleStartDiagnoseTaas,

      handleContinueCheckingChoice,
      iwsAccount,
      macAddress,
    }),
    [
      loading,
      diagnoseStatus,
      loginId,
      nttData,
      openTicketDetails,
      serialNumber,
      ServiceNo,
      accountAction,
      serviceCustomerRowId,
      customerRowIdsForLookup,
      activeServiceAccount,
      selectedSystemName,
      closedTicketDetails,
      contactList,
      contacts,
      askNetworkStatus,
      srDetails,
      setDiagnoseLoading,
      handleStartDiagnose,
      setAccountAction,
      handleStartDiagnoseTaas,
      handleContinueCheckingChoice,
      iwsAccount,
      macAddress,
    ]
  )
  return (
    <DiagnoseContext.Provider value={context}>
      {children}
    </DiagnoseContext.Provider>
  )
}
